import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DropdownList } from 'react-widgets';
import { DateInput } from '../../../components/form-group';
import { combineDateAndTime } from '../../../utils/CombineDateAndTime';
import {
  createStartOfDayWithTimeZone,
  DateFormats,
  getDayDifference,
  getMinuteDifference,
  singleDayTimeBuilder,
} from '../../utils';
import { ITimePeriodData } from './custom-time-period';

interface ICustomTimeInputProps {
  defaultValue?: ITimePeriodData;
  onChange?: (value: ITimePeriodData) => void;
  timeGap?: number;
  gap?: number;
  minDate?: Date;
  disabled?: boolean;
}

export const TimePeriodOnly: React.FunctionComponent<ICustomTimeInputProps> = (
  props
) => {
  const [endData, setEndData] = useState<{ label: string; value: string }[]>(
    []
  );
  const [startData, setStartData] = useState<
    { label: string; value: string }[]
  >([]);
  const startOfToday = new Date();

  const [startDate, setStartDate] = useState<Date>(startOfToday);
  const [startTime, setStartTime] = useState(
    props.defaultValue?.startTime ?? '12:00 AM'
  );
  const [endTime, setEndTime] = useState(
    props.defaultValue?.endTime ?? '11:55 PM'
  );
  const [endDate, setEndDate] = useState<Date>(startOfToday);
  React.useEffect(() => {
    setStartData(singleDayTimeBuilder(undefined, props.gap));
    setEndDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    setEndDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime]);

  useEffect(() => {
    handleOnChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime, endTime]);

  const handleOnChange = () => {
    const date = startDate;
    const value: ITimePeriodData = {
      startDate: date,
      startTime: startTime ? startTime : undefined,
      endDate: date,
      endTime: endTime ? endTime : undefined,
    };
    if (props.onChange) {
      props.onChange(value);
    }
  };

  const setEndDropdownData = () => {
    if (startDate != null && endDate != null && startTime !== '') {
      const startDateTime = combineDateAndTime(
        moment(startDate).startOf('day').toDate(),
        startTime
      );
      setEndData(singleDayTimeBuilder(startDateTime, props.gap));
    }
  };

  React.useEffect(() => {
    if (endDate != null && endTime !== '' && endData.length > 0) {
      const isValidObject = endData.find((p) => p.value === endTime) != null;

      const endDateTime = combineDateAndTime(
        moment(endDate).startOf('day').toDate(),
        endTime
      );
      const startDateTime = combineDateAndTime(
        moment(startDate).startOf('day').toDate(),
        startTime
      );
      const minuteGap = getMinuteDifference(startDateTime, endDateTime);

      if (!isValidObject && minuteGap <= 0) {
        setEndTime(endData[0].value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endData]);

  const handleStartTimeChange = (time: string) => {
    if (
      endTime !== '' &&
      endDate !== undefined &&
      startTime !== '' &&
      startDate !== undefined
    ) {
      const endDateTime = combineDateAndTime(
        moment(endDate).startOf('day').toDate(),
        endTime
      );
      const startDateTime = combineDateAndTime(
        moment(startDate).startOf('day').toDate(),
        startTime
      );

      const minuteGap = getMinuteDifference(startDateTime, endDateTime);
      if (minuteGap <= 0) {
        const newStartDateTime = combineDateAndTime(startDate, time);
        const newEndDateTime = moment(newStartDateTime).add(
          minuteGap,
          'minutes'
        );
        const newEndDate = newEndDateTime.toDate();

        if (moment(startDate).diff(newEndDate, 'day') > 1) {
          setEndTime('11:55 PM');
          setEndDate(startDate);
        } else {
          setEndTime(newEndDateTime.format(DateFormats.TimeOnlyFormat));
          setEndDate(newEndDate);
        }
      }
    }
    setStartTime(time);
  };

  return (
    <div className="custom-time-input">
      <div className="row gutter-8">
        <div className="col-lg-4 mb-lg-0 mb-3">
          <div className="position-relative time-period">
            <DropdownList
              placeholder={'HH:mm'}
              containerClassName="time-period-container"
              textField="label"
              valueField="value"
              //  placeholder={props.placeHolder}
              value={startTime}
              onChange={(val) => {
                handleStartTimeChange(val.value);
              }}
              data={startData}
              disabled={props.disabled}
            />
            {startTime !== '' && !props.disabled && (
              <i
                onClick={() => {
                  setStartTime('');
                }}
                className="icon-close position-absolute bg-white align-y-center font-xxxs text-center p-2 font-weight-bold cursor-pointer time-close"
              ></i>
            )}
          </div>
        </div>

        <div className="col-lg-4 mb-sm-0 mb-3">
          <div className="position-relative time-period">
            <DropdownList
              placeholder={'HH:mm'}
              containerClassName="time-period-container"
              textField="label"
              valueField="value"
              //  placeholder={props.placeHolder}
              value={endTime}
              onChange={(val) => {
                setEndTime(val.value);
              }}
              data={endData}
              disabled={props.disabled}
            />
            {endTime !== '' && !props.disabled && (
              <i
                onClick={() => {
                  setEndTime('');
                }}
                className="icon-close position-absolute bg-white align-y-center font-xxxs text-center p-2 font-weight-bold cursor-pointer time-close"
              ></i>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const isTimePeriodValid = (period: ITimePeriodData) => {
  return period?.startDate && period?.startTime && period.endTime;
};
